/**
 * Automatische SKU-Verknuepfung beim Bestellexcel-Import (Asana-Ticket "Automatische Verknuepfung
 * von Artikelname mit SKU Eingabe", https://app.asana.com/1/1123875322773656/task/1218176087932581).
 *
 * `positionAusExcelZeile` (`PositionsEditor.tsx`) baut jede importierte Zeile bislang OHNE
 * `kArtikel` - Verhaltensaenderung 3 verlangt eine Bestaetigung ueber die JTL-Suche, bevor eine
 * Position nach JTL gehen darf (`pruefeNeueBestellung`, `backend/src/jtl/schreiben/anlegen.ts:91`
 * weist jede Position ohne ganzzahliges `kArtikel >= 1` als Freiposition zurueck). Diese Datei nimmt
 * dem Bediener genau diese Bestaetigung ab, WENN die importierte SKU eindeutig in JTL gefunden wird -
 * eine Zeile ohne (oder mit mehrdeutigem) Treffer bleibt weiterhin unbestaetigt und traegt danach
 * `jtlKeinTreffer: true` (`lib/types.ts#OrderItem`), damit sie von Hand nachgezogen werden kann.
 *
 * ================= REINE FUNKTION, GETRENNT VOM BAUTEIL =================
 *
 * Wie `PositionenZiehen.ts` (Kopfkommentar dort, "Trennung von Rechnung und Wirkung"): dieses Modul
 * RECHNET die neue Positionsliste aus und gibt sie zurueck; `PositionsEditor.tsx
 * #excelDateiVerarbeiten` ruft `onAendern(...)` erst mit dem Ergebnis auf, VOR dem bisherigen
 * `onAendern(entwurf.items.map(positionAusExcelZeile))`. Kein React, keine Seiteneffekte ausser den
 * Netzaufrufen ueber die uebergebene `artikelSuche`-Funktion.
 *
 * ================= DIREKTER AUFRUF, NICHT DIE DEBOUNCE-DROSSEL =================
 *
 * NACHBESSERUNG (Bugreport "JTL-Autoabgleich verknuepft nur einen Bruchteil der SKUs", Damian
 * Gembries, 2026-09-04): dieser Abschnitt behauptete urspruenglich, die von `PositionsEditor.tsx`
 * hereingereichte `artikelSuche`-Funktion sei ein direkter, ungedrosselter Serverabruf - das war
 * FALSCH und war der eigentliche Bug. Tatsaechlich war die dort hereingereichte Prop selbst schon
 * `PositionsEditor.tsx`s `gedrosselt = jtlSuche({ abruf: artikelSuche })` (Constraint 16): EIN
 * geteilter, modulweiter Debounce-Timer (`api/jtlSuche.ts`, `timer`/`wartende`) fuer ALLE Aufrufer.
 * `begrenztAusfuehren` unten reiht bis zu `MAX_GLEICHZEITIGE_SUCHEN = 4` gleichzeitige, VERSCHIEDENE
 * SKUs in genau dieses eine `wartende`-Array ein; feuert der Timer, wird NUR der ZULETZT
 * eingereihte Suchbegriff tatsaechlich abgefragt (`api/jtlSuche.ts:222-226`), und ALLE wartenden
 * Aufrufer - auch die fuer ANDERE SKUs - werden mit GENAU DIESEM (fuer sie falschen) Ergebnis
 * aufgeloest. Deterministisch reproduziert: von sieben SKUs in der Bugreport-Excel wurden nur die
 * zwei zuletzt eingereihten verknuepft, die uebrigen fuenf bekamen `jtlKeinTreffer: true`, obwohl
 * alle sieben real und aktiv in JTL existieren.
 *
 * Der Fix reicht `positionenGegenJtlAbgleichen` seither eine ECHT ROHE Suchfunktion herein -
 * `artikelSucheRoh` (`App.tsx#anlagemaskeArtikelAbruf` -> `Anlagemaske.tsx#positionsArtikelSucheRoh`
 * -> `PositionsEditor.tsx`s `artikelSucheRoh`-Prop), die NICHT ueber `jtlSuche(...)` laeuft. Diese
 * Funktion selbst bleibt jetzt tatsaechlich das, was dieser Abschnitt vorher schon (faelschlich) von
 * der gedrosselten Prop behauptet hat: ein Excel-Import tippt nicht, er kennt seine (eindeutigen)
 * SKUs bereits vollstaendig - `artikelSuche(sku)` wird DIREKT aufgerufen, einmal je eindeutiger,
 * ausreichend langer SKU (Dedup UND Mindestlaengenpruefung unten, VOR dem ersten Aufruf). Die
 * interaktive Zeilensuche (`PositionsEditor.tsx#suchen`) bleibt unveraendert bei der gedrosselten
 * `artikelSuche`/`gedrosselt` - NUR der Excel-Import braucht die rohe Fassung.
 *
 * ================= NUR EXAKTE TREFFER, GROSS-/KLEINSCHREIBUNG UNERHEBLICH =================
 *
 * `/api/jtl/articles` sucht per LIKE ueber cArtNr/Name/Lieferantenartikelnummer
 * (`backend/src/jtl/artikel.ts#sucheArtikel`) - eine Anfrage nach "40056" liefert deshalb auch
 * Teiltreffer wie "40056060-ERSATZ" zurueck. Nur ein Treffer, dessen `sku` (normalisiert wie die
 * Suche selbst - `normalisiereSuchbegriff`, `api/jtlSuche.ts`) EXAKT der gesuchten SKU entspricht,
 * zaehlt hier als Verknuepfung; mehrere exakte Treffer nehmen den ERSTEN (wie eine Trefferliste, aus
 * der ein Bediener ebenfalls nur einen waehlen kann). NACHBESSERUNG (Pruefung von `3588d379`,
 * Punkt 4): SQL Server sucht ohne `COLLATE` case-insensitiv - ein reiner `String(...)`/`.trim()`-
 * Vergleich verwarf einen von JTL gemeldeten, tatsaechlich exakten Treffer bei abweichender
 * Gross-/Kleinschreibung (z. B. "X301017MA" importiert gegen "x301017ma" in JTL - die Manual-/
 * x-Artikel-Familie, `lib/manuelleArtikel.ts`, ist davon besonders betroffen). Beide Seiten des
 * Vergleichs laufen deshalb durch `normalisiereSuchbegriff` (klein geschrieben, Leerraum
 * vereinheitlicht) statt nur durch `.trim()`.
 *
 * ================= MINDESTLAENGE =================
 *
 * `SUCHE_MINDESTLAENGE` (`backend/src/jtl/suchCache.ts`) lehnt einen zu kurzen Suchbegriff mit 400
 * ab - dieselbe Zahl steht im Frontend bereits als `JTL_SUCHE_MINDESTLAENGE` (`api/jtlSuche.ts`),
 * von dort uebernommen statt abgeschrieben. Eine leere oder zu kurze SKU wird deshalb GAR NICHT erst
 * angefragt und zaehlt direkt als "kein Treffer".
 *
 * ================= DER EXCEL-PREIS BLEIBT IMMER STEHEN =================
 *
 * NACHBESSERUNG (Pruefung von `3588d379`, Punkt 1 - KRITISCH): anders als `uebernehmen()`
 * (`PositionsEditor.tsx`, eine NUTZERAUSGELOESTE manuelle Auswahl, bei der die Preisuebernahme aus
 * JTL ein bewusster Schritt ist) darf der AUTOMATISCHE Abgleich den fuer DIESE Bestellung
 * verhandelten Excel-Preis nicht durch den JTL-Stammpreis ersetzen - unabhaengig davon, ob
 * `treffer.price` gesetzt ist. Der urspruengliche Stand uebernahm `treffer.price`, sobald JTL einen
 * lieferte, und liess damit einen abweichenden Bestellpreis lautlos im JTL-Beleg verschwinden
 * (`App.tsx:4409`). `positionMitTreffer` unten setzt `price` deshalb IMMER auf `item.price` - dieses
 * eine Feld ist bewusst NICHT dieselbe Formel wie in `uebernehmen()`, jedes andere Feld schon.
 *
 * ================= BEGRENZTE NEBENLAEUFIGKEIT GEGEN DEN JTL-POOL =================
 *
 * NACHBESSERUNG (Pruefung von `3588d379`, Punkt 3 - WICHTIG): der JTL-Lesepool erlaubt hoechstens
 * `JTL_MAX_VERBINDUNGEN = 2` gleichzeitige Verbindungen (`backend/src/jtl/pool.ts`,
 * `acquireTimeoutMillis: 5000`) - Cache und Drossel der interaktiven Suche halten die Last dort
 * normalerweise klein (Kopfkommentar von `pool.ts`, woertlich: "zwei Verbindungen reichen nur WEIL
 * Cache und Drossel die Last vorher brechen"). Ein `Promise.all` ueber ALLE eindeutigen SKUs auf
 * einmal (der urspruengliche Stand) unterlaeuft genau diese Annahme: ein Bestellexcel mit z. B. 80
 * verschiedenen SKUs haette 80 gleichzeitige Anfragen ausgeloest, die hinteren liefen in den
 * Pool-Zeitgrenzenfehler und waeren (ueber den Fehlerfang unten) faelschlich als "kein Treffer"
 * verbucht worden - UND haette gleichzeitig suchende Kolleginnen und Kollegen mitgebremst.
 * `begrenztAusfuehren` unten haelt die Zahl gleichzeitig ausstehender Anfragen auf
 * `MAX_GLEICHZEITIGE_SUCHEN`; kein neues Paket, ein winziger Worker-Pool reicht (im Repo existiert
 * keine generische Nebenlaeufigkeitsgrenze zum Wiederverwenden - `PositionenZiehen.ts` & Co. loesen
 * andere Probleme).
 *
 * ================= EIN FEHLGESCHLAGENER SUCHAUFRUF BLOCKIERT DEN IMPORT NICHT =================
 *
 * `excelDateiVerarbeiten` ruft diese Funktion VOR `onAendern` auf - eine unbehandelte Zurueckweisung
 * wuerde dort im bestehenden `catch`-Zweig landen und den kompletten (bereits erfolgreich
 * ausgelesenen) Excel-Import als Fehlschlag anzeigen, obwohl nur der JTL-Abgleich einer einzelnen SKU
 * gescheitert ist. Ein Fehler bei EINEM Suchaufruf zaehlt deshalb wie kein Treffer fuer die
 * betroffene(n) SKU - die uebrigen Zeilen bleiben unberuehrt.
 *
 * FEHLERANZEIGE PAKET 2 (Task 8): bis dahin zaehlte der Fehler "still wie kein Treffer" - die Zeile
 * trug `jtlKeinTreffer: true` und behauptete damit, JTL kenne die SKU nicht, obwohl die Suche gar nicht
 * lief (Server weg, Netz weg, unbrauchbare Antwort). Jetzt bekommt die betroffene Zeile einen EIGENEN
 * Status, `jtlSucheFehler: { ursache }` (`lib/types.ts#OrderItem`), den `PositionsEditor.tsx` als "JTL-Suche
 * gescheitert" samt Ursache an der Zeile zeigt - dort ist die Flaeche, an der der Fehlertext haengt (Regel
 * L1, kein Stapeleintrag). Der Import bleibt ein Erfolg, die Zeile unbestaetigt und von Hand verknuepfbar,
 * wie jede Zeile ohne Treffer. `ursache` ist der fertige Nutzersatz aus `fehlertext()` (ohne Kontext), `null`
 * bei einem 401 (die Anmeldemaske uebernimmt, Regel L5). Der Status hat Vorrang vor der Preispruefung unten
 * und schliesst `jtlKeinTreffer` aus: "kein Treffer" bleibt der Suche vorbehalten, die gelang und nichts fand.
 * Mehrere Zeilen mit derselben SKU teilen die eine Suche und damit auch ihr Scheitern. NACHBESSERUNG (Pruefung von `3588d379`,
 * Punkt 5 - GERING): der `try` umschliesst jetzt den Suchaufruf UND die anschliessende Treffersuche
 * (`exakterTreffer`) - der urspruengliche Stand liess `.find(...)` AUSSERHALB des Fehlerfangs laufen;
 * eine Antwort, an der `.find` selbst wirft (z. B. kein Array), waere unbehandelt bis in
 * `excelDateiVerarbeiten` durchgereicht worden. Eine solche Antwort gilt seit Task 8 als gescheiterte Suche
 * (Sammeltext "Unerwarteter Fehler", der Programmfehler selbst geht per `fehlertext()` in die Konsole).
 *
 * ================= KEINE AUTO-BESTAETIGUNG OHNE PREIS =================
 *
 * NACHBESSERUNG 2 (zweite Pruefung, vom Auftraggeber bestaetigt): `Number("")` ist `0`, und
 * `orderExcelNumber()` (`lib/format.ts`) liefert bei einer Excel-Zeile OHNE erkennbare Preisspalte
 * genau `""`. Ohne diesen Fix bestaetigte ein exakter SKU-Treffer eine solche Zeile trotzdem
 * automatisch (`kArtikel` gesetzt, kein roter Hinweis) - und sie ging mit `fEKNetto = 0` in den
 * JTL-Beleg (`App.tsx:4409-4412`): ein stiller Datenfehler in einem Einkaufsbeleg, den es VOR dem
 * Autoabgleich strukturell nicht geben konnte (jede Zeile brauchte einen manuellen
 * Bestaetigungsklick, bei dem ein leeres Preisfeld auffiel). `hatErkennbarenPreis` unten gate't
 * deshalb NUR die abschliessende Zuordnung je Zeile (nicht die Suche selbst - die laeuft weiterhin
 * fuer jede eindeutige SKU, unabhaengig vom Preis, siehe `positionenGegenJtlAbgleichen`): eine Zeile
 * ohne erkennbaren Preis wird wie eine Zeile OHNE Treffer behandelt (`jtlKeinTreffer: true`,
 * unveraendert), selbst wenn die SKU exakt trifft. BEWUSST NICHT geprueft: ob der Preis `"0"` ist -
 * ein EXPLIZIT importierter Nullpreis ist etwas anderes als ein FEHLENDER Preis (Auftragsvorgabe,
 * woertlich) und bleibt unangetastet; `"0".trim() !== ""` laesst ihn folgerichtig durch.
 */
import { istManuellerArtikel } from "../../lib/manuelleArtikel.ts";
import { JTL_SUCHE_MINDESTLAENGE, normalisiereSuchbegriff } from "../../api/jtlSuche.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import type { OrderItem } from "../../lib/types.ts";
import type { JtlArtikelTreffer } from "./PositionsEditor.tsx";

/**
 * Obergrenze gleichzeitig ausstehender `artikelSuche`-Aufrufe (siehe Kopfkommentar, "BEGRENZTE
 * NEBENLAEUFIGKEIT GEGEN DEN JTL-POOL"). Kein Bezug 1:1 zu `JTL_MAX_VERBINDUNGEN = 2` - diese Grenze
 * gilt auf Browserseite fuer EINEN Import, waehrend der Pool serverseitig ALLE gleichzeitigen
 * Bedienerinnen und Bediener bedient; ein kleines Vielfaches (statt exakt 2) haelt den Import zuegig
 * und laesst dem Pool durch Cache/Single-Flight noch Luft.
 */
const MAX_GLEICHZEITIGE_SUCHEN = 4;

/**
 * Fuehrt `aufgabe` fuer jeden Eintrag aus `eintraege` aus, dabei nie mehr als `grenze` Aufrufe
 * gleichzeitig ausstehend - ein winziger Worker-Pool: `grenze` "Arbeiter" ziehen sich Eintraege
 * nacheinander aus einem gemeinsamen Index, bis keiner mehr uebrig ist. Reihenfolge der ERGEBNISSE
 * ist hier ohne Belang (`aufgabe` traegt ihr Ergebnis selbst ein, siehe Aufrufer unten).
 */
async function begrenztAusfuehren<T>(
  eintraege: readonly T[],
  grenze: number,
  aufgabe: (eintrag: T) => Promise<void>,
): Promise<void> {
  let naechsterIndex = 0;
  async function arbeiter(): Promise<void> {
    while (naechsterIndex < eintraege.length) {
      const eintrag = eintraege[naechsterIndex]!;
      naechsterIndex += 1;
      await aufgabe(eintrag);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(grenze, eintraege.length) }, () => arbeiter()),
  );
}

/** Der getrimmte SKU-Wert einer Position - `""` bei einer leeren/fehlenden SKU. */
function skuVon(item: Pick<OrderItem, "sku">): string {
  return String(item.sku ?? "").trim();
}

/**
 * Traegt die Zeile einen aus dem Excel-Import ERKENNBAREN Preis? Siehe Kopfkommentar, "KEINE
 * AUTO-BESTAETIGUNG OHNE PREIS" - `false` bei leerem/nur-Leerraum-Preis, `true` bei jedem anderen
 * Wert, AUSDRUECKLICH auch bei `"0"` (ein expliziter Nullpreis ist kein fehlender Preis).
 */
function hatErkennbarenPreis(item: Pick<OrderItem, "price">): boolean {
  return String(item.price ?? "").trim() !== "";
}

/**
 * Der erste Treffer aus `liste`, dessen `sku` NORMALISIERT (wie `artikelSuche` selbst - Gross-/
 * Kleinschreibung und Leerraum unerheblich, siehe Kopfkommentar) `sku` entspricht - `sku` MUSS bereits
 * normalisiert hereinkommen (Aufrufer unten normalisiert einmal, nicht bei jedem Listeneintrag neu).
 * `undefined`, wenn keiner passt (nur Teiltreffer der LIKE-Suche oder eine leere Liste).
 */
function exakterTreffer(
  liste: readonly JtlArtikelTreffer[],
  normalisierteSku: string,
): JtlArtikelTreffer | undefined {
  return liste.find(
    (treffer) => normalisiereSuchbegriff(String(treffer.sku ?? "")) === normalisierteSku,
  );
}

/**
 * Wendet einen gefundenen Treffer auf EINE Position an - dieselben Felder, die `uebernehmen()`
 * (`PositionsEditor.tsx`) bei einer manuellen Auswahl setzt, BIS AUF `price`: siehe Kopfkommentar,
 * "DER EXCEL-PREIS BLEIBT IMMER STEHEN". `qty` bleibt ebenfalls unangetastet (kommt aus dem
 * Excel-Import, nicht aus JTL).
 */
function positionMitTreffer(item: OrderItem, treffer: JtlArtikelTreffer): OrderItem {
  return {
    ...item,
    name: String(treffer.name ?? ""),
    sku: String(treffer.sku ?? ""),
    manual: istManuellerArtikel(treffer.sku),
    // BEWUSST NICHT `treffer.price != null ? String(treffer.price) : item.price` (die Formel aus
    // `uebernehmen()`) - der automatische Abgleich uebernimmt den JTL-Preis NIE, siehe Kopfkommentar.
    price: item.price,
    kArtikel: treffer.id != null && String(treffer.id) !== "" ? Number(treffer.id) : null,
    mwstProzent: treffer.mwstProzent != null ? Number(treffer.mwstProzent) : null,
    hatBild: treffer.hatBild as boolean | undefined,
    verknuepft: treffer.verknuepft as boolean | null | undefined,
    // Bei einem Treffer ist die Zeile bestaetigt - ein zuvor gesetztes "kein Treffer" darf nicht
    // stehen bleiben (relevant, sobald dieselbe Liste ein zweites Mal durch den Abgleich laeuft).
    jtlKeinTreffer: undefined,
    // Ebenso ein frueherer Status "Suche gescheitert" (Fehleranzeige Paket 2, Task 8): beide Zustaende
    // schliessen einander und einen Treffer aus.
    jtlSucheFehler: undefined,
  };
}

/**
 * Gleicht `positionen` gegen JTL ab: jede Zeile mit einer nicht-leeren, ausreichend langen SKU wird
 * EINMAL je eindeutiger SKU angefragt (Dedup vor dem Aufruf - mehrere Zeilen koennen dieselbe SKU
 * tragen), mit hoechstens `MAX_GLEICHZEITIGE_SUCHEN` gleichzeitig ausstehenden Anfragen. Ein exakter
 * Treffer bestaetigt eine Zeile automatisch (dieselben Felder wie `uebernehmen()`, ausser `price`)
 * NUR, wenn sie zusaetzlich einen erkennbaren Preis traegt (`hatErkennbarenPreis`, Nachbesserung 2).
 * Ohne (exakten, preisbestaetigten) Treffer bleibt die Zeile UNVERAENDERT (1:1 wie importiert) und
 * bekommt zusaetzlich `jtlKeinTreffer: true`. Ist die Suche fuer ihre SKU GESCHEITERT, bekommt sie
 * stattdessen `jtlSucheFehler` (siehe Kopfkommentar, "EIN FEHLGESCHLAGENER SUCHAUFRUF ...").
 */
export async function positionenGegenJtlAbgleichen(
  positionen: readonly OrderItem[],
  artikelSuche: (begriff: string) => Promise<JtlArtikelTreffer[]>,
): Promise<OrderItem[]> {
  const eindeutigeSkus = new Set<string>();
  for (const item of positionen) {
    const sku = skuVon(item);
    if (sku.length >= JTL_SUCHE_MINDESTLAENGE) eindeutigeSkus.add(sku);
  }

  const treffer = new Map<string, JtlArtikelTreffer>();
  /** SKU -> Nutzersatz zur Ursache (`null` bei 401/Abbruch) fuer jede SKU, deren Suche gescheitert ist. */
  const gescheitert = new Map<string, string | null>();
  await begrenztAusfuehren(Array.from(eindeutigeSkus), MAX_GLEICHZEITIGE_SUCHEN, async (sku) => {
    try {
      // Suchaufruf UND Treffersuche IM SELBEN try-Block (Nachbesserungspunkt 5) - eine Antwort, an
      // der `exakterTreffer` selbst wirft (z. B. keine Liste), gilt damit ebenfalls als gescheiterte
      // Suche statt den ganzen Import zu gefaehrden (siehe Kopfkommentar).
      const liste = await artikelSuche(sku);
      const gefunden = exakterTreffer(liste, normalisiereSuchbegriff(sku));
      if (gefunden) treffer.set(sku, gefunden);
    } catch (fehler) {
      // Siehe Kopfkommentar, "EIN FEHLGESCHLAGENER SUCHAUFRUF BLOCKIERT DEN IMPORT NICHT": der Import laeuft
      // weiter, die Zeile bekommt aber den Status "gescheitert" samt Ursache statt "kein Treffer".
      gescheitert.set(sku, fehlertext(fehler));
    }
  });

  return positionen.map((item) => {
    const sku = skuVon(item);
    // Eine gescheiterte Suche ist KEIN "kein Treffer" und hat Vorrang vor der Preispruefung unten: sie sagt,
    // dass der Abgleich fuer diese SKU nicht lief - unabhaengig davon, ob die Zeile einen Preis traegt.
    if (gescheitert.has(sku)) {
      return {
        ...item,
        jtlKeinTreffer: undefined,
        jtlSucheFehler: { ursache: gescheitert.get(sku) ?? null },
      };
    }
    // NACHBESSERUNG 2: ein gefundener Treffer bestaetigt die Zeile NUR, wenn sie einen erkennbaren
    // Preis traegt - siehe Kopfkommentar, "KEINE AUTO-BESTAETIGUNG OHNE PREIS". Die Suche selbst lief
    // fuer die SKU trotzdem (oben, ungegatet) - andere Zeilen mit derselben SKU, aber gesetztem
    // Preis, profitieren so weiterhin vom selben (gecachten) Treffer.
    const gefunden = hatErkennbarenPreis(item) ? treffer.get(sku) : undefined;
    return gefunden
      ? positionMitTreffer(item, gefunden)
      : { ...item, jtlKeinTreffer: true, jtlSucheFehler: undefined };
  });
}
